import { MutableRefObject } from 'react'
import { AccessibilityPropsAndroid, LayoutChangeEvent, ViewStyle } from 'react-native'
import type { Edges } from 'react-native-safe-area-context'

import { CSSObject } from 'styled-components'

export type ProgressBarProps = {
  progress?: number
  height?: number
  color?: string
  borderRadius?: number
  backgroundColor?: string | ArrayMinTwo<string>
  shouldAnimate?: boolean
  width?: number | string
  style?: ViewStyle
  duration?: number
  accessibilityLiveRegion?: AccessibilityPropsAndroid['accessibilityLiveRegion']
  showStopIndicator?: boolean
}

export type FavoritedProvidersEntrypointNavProps = {
  isFavoritedProvidersStarIconFilled: boolean
  shouldShowFavoritedProvidersStarIcon: boolean
  onFavoritedProvidersStarIconPressed: ({ isFromHamburgerMenu }: { isFromHamburgerMenu: boolean }) => void
  onHoverForIconExplanation?: () => void
}

export type NavBarProps = {
  isTransparent?: boolean
  title?: string | JSX.Element | React.ReactNode | null
  titleLevel?: '2' | '3' | '4'
  backgroundColor?: string
  borderBottomColor?: string
  noBottomBorder?: boolean
  showBackButton?: boolean
  showCloseButton?: boolean
  showProgressBar?: boolean
  style?: ViewStyle
  a11yFocusedElementOnLoad?: MutableRefObject<any>
  shouldShowCareNavigatorHeaderEntrypoint?: boolean
  careNavigatorModal?: React.ReactElement
  careNavigatorEntryPoint?: React.ReactElement
  isInternational?: boolean
  testId?: string
  backIconColor?: string
  useTitle?: boolean
  alternateExitButtonText?: React.ReactNode
  showLogoMobileSized?: boolean
  customRightComponent?: React.ReactNode
  isFixed?: boolean
  progressBarProps?: ProgressBarProps
  hideLogo?: boolean
  onBack?: (...args: any) => void
  onExit?: (...args: any) => void
  onSkip?: () => void
  onLayout?: (e: LayoutChangeEvent) => void
  onCareNavigatorHeaderEntryPointButtonPress?: () => void
  onLogoPress?: () => void
  favoritedProvidersEntrypointNavProps?: FavoritedProvidersEntrypointNavProps
  customStyles?: {
    navBarContent?: CSSObject
    titleContainer?: CSSObject
  }
  isBlurBackground?: boolean
}

export type GenericHeaderProps = {
  title?: React.ReactNode
  onBack?: (...args: any) => void
  onExit?: (...args: any) => void
  backgroundColor?: string
  foregroundColor?: string
  testIdPrefix?: string
  isSmall?: boolean
  style?: ViewStyle
  a11yFocusedElementOnLoad?: MutableRefObject<any>
  shouldShowCareNavigatorHeaderEntrypoint?: boolean
  careNavigatorModal?: React.ReactElement
  careNavigatorEntryPoint?: React.ReactElement
  favoritedProvidersEntrypointNavProps?: FavoritedProvidersEntrypointNavProps
  openCareNavigatorModal?: () => void
  isInternational?: boolean
  edges?: Edges
  hasBottomBorder?: boolean

  /**
   * Used by some flows that pass a single `headerProps` bag to both `GenericHeader` and `NavBar`.
   * These props are ignored by `GenericHeader`, but are consumed by `NavBar`.
   */
  showProgressBar?: boolean
  progressBarProps?: ProgressBarProps
}

export enum HEADER_NAV_ITEM {
  BENEFITS = 'headerNavBenefitsButton',
  ACCOUNT = 'headerNavAccountButton',
  FAQS = 'headerNavFAQsButton',
  REFERRAL = 'headerNavReferralButton',
  LOG_OUT = 'headerNavLogOutButton',
  HOME = 'headerNavHomeButton',
  CARE = 'headerNavCareButton',
  SESSIONS = 'headerNavSessionsButton',
  PROGRESS = 'headerNavProgressButton',
  ESSENTIALS = 'headerNavEssentialsButton',
  FAVORITED_PROVIDERS = 'headerNavFavoritedProvidersButton',
  CARE_NAVIGATOR = 'headerNavCareNavigatorButton',
}

/** Which UI surface triggered a Care Navigator modal open — drives mixpanel attribution. */
export enum CARE_NAVIGATOR_TRIGGER_SOURCE {
  NAV_BAR = 'navbar',
  HAMBURGER = 'hamburger',
}

export interface NavigationItem {
  text: string
  id: string
  testId?: string
  onPressItem?: () => void
  condition?: () => boolean
  activePathnames?: string[]
  activeScreens?: string[]
  /**
   * Icon renderer for standard TabBar — receives isActive boolean.
   * Used in non-Aurora themes.
   */
  icon?: (isActive: boolean) => React.ReactElement
  /**
   * Icon renderer for FloatingTabBar — receives the iconColor directly.
   * Used in Aurora theme. Falls back to `icon` if not provided.
   */
  floatingIcon?: (iconColor: string) => React.ReactElement
}
